import type { ISafeAreaInsets, IStorageResponse } from '../features/nativeBridge';

/**
 * Default event map interface for type safety.
 * Extend this interface to define your own event types.
 */
export interface IDefaultEventMap {
  [key: string]: unknown;
  SAFE_AREA_INSETS: ISafeAreaInsets;
  STORAGE_RESPONSE: IStorageResponse;
}

/**
 * Type-safe event emitter class.
 * @template Events - Event map type that defines event names and their argument types.
 */
class EventEmitter<Events extends IDefaultEventMap = IDefaultEventMap> {
  private events: {
    [K in keyof Events]?: Array<(arg: Events[K]) => void>;
  } = {};

  /**
   * Subscribe to an event's notification list.
   * @param event - The event to subscribe to.
   * @param listener - The listener to call when the event is emitted.
   * @returns A function to unsubscribe from the event.
   */
  on<K extends keyof Events>(event: K, listener: (arg: Events[K]) => void) {
    if (!this.events[event]) this.events[event] = [];
    this.events[event]!.push(listener);
    return () => this.off(event, listener); // Unsubscribe function.
  }

  /**
   * Remove a listener from an event's notification list.
   * @param event - The event to unsubscribe from.
   * @param listener - The listener to remove.
   */
  off<K extends keyof Events>(event: K, listener: (arg: Events[K]) => void) {
    if (!this.events[event]) return;
    this.events[event] = this.events[event]!.filter((l) => l !== listener);
  }

  /**
   * Emit an event. Notify all listeners of the event.
   * @param event - The event to emit.
   * @param arg - The argument to pass to the event listeners.
   */
  emit<K extends keyof Events>(event: K, arg: Events[K]) {
    if (!this.events[event]) return;
    this.events[event]!.forEach((listener) => listener(arg));
  }
}

// Export a singleton instance with default event map.
export const eventEmitter = new EventEmitter();
